import React, { FC } from 'react';

import './DataTypeIcon.css';

export const DataType = {
  UNKNOWN: 'unknown',
  STRING: 'string',
  INT64: 'int64',
  FLOAT64: 'float64',
  MAP: 'map',
  JSON: 'json',
  FILE: 'file',
  BOOLEAN: 'boolean',
} as const;

interface IDataTypeIcon {
  id?: string;
  key?: string;
  className?: string;
  dataType?: string;
}

export const DataTypeIcon: FC<IDataTypeIcon> = ({
  dataType,
  className,
  ...others
}) => {
  const UnknownRenderer = () => {
    return (
      <div
        data-testid="datatype-icon-unknown"
        className={`datatype-icon datatype-icon-unknown ${className || ''}`}
        {...others}
      >
        ?
      </div>
    );
  };

  const StringRenderer = () => {
    return (
      <div
        data-testid="datatype-icon-string"
        className={`datatype-icon datatype-icon-string ${className || ''}`}
        {...others}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="12"
          viewBox="0 0 10 12"
          fill="none"
        >
          <path
            d="M2.66359 11.6051V10.2579L3.86451 10.027V2.12859H1.82448L1.70131 3.46808H0V0.396484H10V3.46808H8.29099L8.16782 2.12859H6.11239V10.027L7.32102 10.2579V11.6051H2.66359Z"
            fill="#FFD966"
          />
        </svg>
      </div>
    );
  };

  const Int64Renderer = () => {
    return (
      <div
        data-testid="datatype-icon-int64"
        className={`datatype-icon datatype-icon-number ${className || ''}`}
        {...others}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
        >
          <path
            d="M9.3083 3.5526H8.01567L7.23471 6.44082H8.5408L8.24458 7.53148H6.95194L6.35949 9.71279H5.22844L5.82089 7.53148H2.82495L2.23922 9.71279H1.10817L1.6939 7.53148H0.394531L0.690759 6.44082H1.98339L2.76436 3.5526H1.45826L1.75449 2.46194H3.05385L3.63284 0.287354H4.7639L4.1849 2.46194H7.18085L7.75311 0.287354H8.88416L8.3119 2.46194H9.60453L9.3083 3.5526ZM6.10365 6.44082L6.88462 3.5526H3.89541L3.11444 6.44082H6.10365Z"
            fill="#00B14F"
          />
        </svg>
      </div>
    );
  };

  const Float64Renderer = () => {
    return (
      <div
        data-testid="datatype-icon-float64"
        className={`datatype-icon datatype-icon-number ${className || ''}`}
        {...others}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
        >
          <path
            d="M9.3083 3.5526H8.01567L7.23471 6.44082H8.5408L8.24458 7.53148H6.95194L6.35949 9.71279H5.22844L5.82089 7.53148H2.82495L2.23922 9.71279H1.10817L1.6939 7.53148H0.394531L0.690759 6.44082H1.98339L2.76436 3.5526H1.45826L1.75449 2.46194H3.05385L3.63284 0.287354H4.7639L4.1849 2.46194H7.18085L7.75311 0.287354H8.88416L8.3119 2.46194H9.60453L9.3083 3.5526ZM6.10365 6.44082L6.88462 3.5526H3.89541L3.11444 6.44082H6.10365Z"
            fill="#00B14F"
          />
        </svg>
      </div>
    );
  };

  const MapRenderer = () => {
    return (
      <div
        data-testid="datatype-icon-map"
        className={`datatype-icon datatype-icon-object ${className || ''}`}
        {...others}
      >
        {`{}`}
      </div>
    );
  };

  const JSONRenderer = () => {
    return (
      <div
        data-testid="datatype-icon-json"
        className={`datatype-icon datatype-icon-object ${className || ''}`}
        {...others}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M2.24386 6.01192C2.67586 6.09192 2.98386 6.26792 3.16786 6.53992C3.35986 6.81192 3.45586 7.20392 3.45586 7.71592V9.07192C3.45586 9.40792 3.50386 9.65192 3.59986 9.80392C3.69586 9.95592 3.87186 10.0319 4.12786 10.0319H5.24386V11.1839H3.99586C3.27586 11.1839 2.77586 11.0279 2.49586 10.7159C2.22386 10.4039 2.08786 9.95192 2.08786 9.35992V7.59592C2.08786 7.25992 2.03586 7.00792 1.93186 6.83992C1.82786 6.66392 1.65186 6.57592 1.40386 6.57592H0.755859V5.42392H1.40386C1.65186 5.42392 1.82786 5.33992 1.93186 5.17192C2.03586 4.99592 2.08786 4.74392 2.08786 4.41592V2.63992C2.08786 2.04792 2.22386 1.59592 2.49586 1.28392C2.77586 0.971918 3.27586 0.815918 3.99586 0.815918H5.24386V1.96792H4.12786C3.87186 1.96792 3.69586 2.04392 3.59986 2.19592C3.50386 2.33992 3.45586 2.58392 3.45586 2.92792V4.28392C3.45586 4.79592 3.35986 5.18792 3.16786 5.45992C2.98386 5.73192 2.67586 5.91592 2.24386 6.01192Z"
            fill="#005339"
          />
          <path
            d="M9.75586 5.98792C9.32386 5.90792 9.01186 5.73192 8.81986 5.45992C8.62786 5.18792 8.53186 4.79592 8.53186 4.28392V2.92792C8.53186 2.59192 8.48386 2.34792 8.38786 2.19592C8.29186 2.04392 8.11586 1.96792 7.85986 1.96792H6.75586V0.815918H7.99186C8.35186 0.815918 8.65186 0.855918 8.89186 0.935919C9.13986 1.01592 9.33586 1.13192 9.47986 1.28392C9.63186 1.43592 9.73986 1.62792 9.80386 1.85992C9.86786 2.08392 9.89986 2.34392 9.89986 2.63992V4.40392C9.89986 4.73992 9.95186 4.99592 10.0559 5.17192C10.1599 5.33992 10.3359 5.42392 10.5839 5.42392H11.2439V6.57592H10.5839C10.3359 6.57592 10.1599 6.66392 10.0559 6.83992C9.95186 7.00792 9.89986 7.25592 9.89986 7.58392V9.35992C9.89986 9.65592 9.86786 9.91592 9.80386 10.1399C9.73986 10.3719 9.63186 10.5639 9.47986 10.7159C9.33586 10.8679 9.13986 10.9839 8.89186 11.0639C8.65186 11.1439 8.35186 11.1839 7.99186 11.1839H6.75586V10.0319H7.85986C8.11586 10.0319 8.29186 9.95592 8.38786 9.80392C8.48386 9.65992 8.53186 9.41592 8.53186 9.07192V7.71592C8.53186 7.20392 8.62786 6.81192 8.81986 6.53992C9.01186 6.26792 9.32386 6.08392 9.75586 5.98792Z"
            fill="#005339"
          />
        </svg>
      </div>
    );
  };

  const FileRenderer = () => {
    return (
      <div
        data-testid="datatype-icon-file"
        className={`datatype-icon datatype-icon-string ${className || ''}`}
        {...others}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="12"
          viewBox="0 0 10 12"
          fill="none"
        >
          <path
            d="M2.66359 11.6051V10.2579L3.86451 10.027V2.12859H1.82448L1.70131 3.46808H0V0.396484H10V3.46808H8.29099L8.16782 2.12859H6.11239V10.027L7.32102 10.2579V11.6051H2.66359Z"
            fill="#FFD966"
          />
        </svg>
      </div>
    );
  };

  const BooleanRenderer = () => {
    return (
      <div
        data-testid="datatype-icon-boolean"
        className={`datatype-icon datatype-icon-boolean ${className || ''}`}
        {...others}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="10"
          viewBox="0 0 14 10"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.5 1.5H9.5C11.433 1.5 13 3.067 13 5C13 6.933 11.433 8.5 9.5 8.5H4.5C2.567 8.5 1 6.933 1 5C1 3.067 2.567 1.5 4.5 1.5ZM0 5C0 2.51472 2.01472 0.5 4.5 0.5H9.5C11.9853 0.5 14 2.51472 14 5C14 7.48528 11.9853 9.5 9.5 9.5H4.5C2.01472 9.5 0 7.48528 0 5ZM4.5 7.5C5.88071 7.5 7 6.38071 7 5C7 3.61929 5.88071 2.5 4.5 2.5C3.11929 2.5 2 3.61929 2 5C2 6.38071 3.11929 7.5 4.5 7.5Z"
            fill="#5547A1"
          />
        </svg>
      </div>
    );
  };

  const DataTypeIconRenderer = () => {
    switch (dataType) {
      case DataType.UNKNOWN:
        return UnknownRenderer();
      case DataType.STRING:
        return StringRenderer();
      case DataType.INT64:
        return Int64Renderer();
      case DataType.FLOAT64:
        return Float64Renderer();
      case DataType.MAP:
        return MapRenderer();
      case DataType.JSON:
        return JSONRenderer();
      case DataType.BOOLEAN:
        return BooleanRenderer();
      case DataType.FILE:
        return FileRenderer();
      default:
        return UnknownRenderer();
    }
  };

  return DataTypeIconRenderer();
};

DataTypeIcon.defaultProps = {
  dataType: DataType.UNKNOWN,
};
